// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useEffect, lazy, Suspense } from 'react';
import { apiClient } from '../../../services/api-client.js';

/**
 * UI Assist context provider.
 * Checks feature flag + permission, then conditionally renders the overlay.
 * Uses dynamic import for zero overhead when disabled.
 * Sprint 17.8.0: UI Assist
 */

const UIAssistOverlay = lazy(() =>
  import('./UIAssistOverlay.js').then((m) => ({ default: m.UIAssistOverlay })),
);

interface UIAssistStatus {
  enabled: boolean;
  hasPermission: boolean;
  wsUrl: string | null;
}

export function UIAssistProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<UIAssistStatus | null>(null);

  useEffect(() => {
    let mounted = true;

    apiClient
      .getUIAssistStatus()
      .then((data) => {
        if (mounted) setStatus(data);
      })
      .catch(() => {
        // Feature not available — silently disable
        if (mounted) setStatus({ enabled: false, hasPermission: false, wsUrl: null });
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      {children}
      {status?.enabled && status?.hasPermission && (
        <Suspense fallback={null}>
          <UIAssistOverlay />
        </Suspense>
      )}
    </>
  );
}
